import { useState } from 'react';
import axios from 'axios';
import { FiCreditCard, FiPlus } from 'react-icons/fi';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PLAN_LABELS = {
    monthly: 'Monthly ₹500',
    annual: 'Annual ₹5,000',
    child_monthly: "Children's monthly ₹300",
    child_annual: "Children's annual ₹3,000",
};
const PLAN_DEFAULTS = { monthly: 500, annual: 5000, child_monthly: 300, child_annual: 3000 };

export default function PaymentHistoryPanel({ userId, payments = [], getAuthHeader, onChanged }) {
    const [formOpen, setFormOpen] = useState(false);
    const [plan, setPlan] = useState('monthly');
    const [amount, setAmount] = useState('500');
    const [method, setMethod] = useState('cash');
    const [notes, setNotes] = useState('');
    const [paidOn, setPaidOn] = useState('');
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    const submit = async () => {
        const amt = parseFloat(amount);
        if (isNaN(amt) || amt < 0) { setError('Enter a valid amount (0 or more)'); return; }
        setSaving(true);
        setError('');
        try {
            const res = await axios.post(`${API}/admin/users/${userId}/record-payment`,
                { plan, amount: amt, method, notes: notes.trim() || null, paid_on: paidOn || null },
                { headers: getAuthHeader() });
            alert(res.data?.message || 'Payment recorded');
            setFormOpen(false);
            setNotes('');
            onChanged?.();
        } catch (e) {
            setError(e.response?.data?.detail || 'Failed to record payment');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="mt-4 p-3 bg-emerald-50 rounded-lg border border-emerald-200" data-testid="payment-history-panel">
            <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2 text-sm font-semibold text-emerald-900">
                    <FiCreditCard /> Payment history ({payments.length})
                </div>
                <button
                    onClick={() => { setFormOpen(!formOpen); setError(''); }}
                    className="px-3 py-1 bg-emerald-600 hover:bg-emerald-700 text-white text-xs rounded flex items-center gap-1"
                    data-testid="record-payment-btn"
                >
                    <FiPlus /> Record payment
                </button>
            </div>

            {formOpen && (
                <div className="mb-3 p-3 bg-white rounded-lg border border-emerald-200 space-y-2" data-testid="record-payment-form">
                    <div className="grid grid-cols-2 gap-2">
                        <select
                            value={plan}
                            onChange={(e) => { setPlan(e.target.value); setAmount(String(PLAN_DEFAULTS[e.target.value])); }}
                            className="border border-gray-300 rounded px-2 py-1.5 text-sm"
                            data-testid="record-payment-plan"
                        >
                            {Object.entries(PLAN_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                        </select>
                        <input
                            type="number" min="0" step="1" value={amount} onChange={(e) => setAmount(e.target.value)}
                            placeholder="Amount ₹" className="border border-gray-300 rounded px-2 py-1.5 text-sm"
                            data-testid="record-payment-amount"
                        />
                        <select value={method} onChange={(e) => setMethod(e.target.value)} className="border border-gray-300 rounded px-2 py-1.5 text-sm" data-testid="record-payment-method">
                            <option value="cash">Cash</option>
                            <option value="upi">UPI</option>
                            <option value="bank_transfer">Bank transfer</option>
                            <option value="card">Card</option>
                            <option value="other">Other</option>
                        </select>
                        <input
                            type="text" value={notes} onChange={(e) => setNotes(e.target.value)}
                            placeholder="Notes (optional)" className="border border-gray-300 rounded px-2 py-1.5 text-sm"
                            data-testid="record-payment-notes"
                        />
                        <label className="col-span-2 text-xs text-gray-600 flex flex-col gap-1">
                            Paid on (optional — leave blank to auto-date)
                            <input
                                type="date" value={paidOn} max={new Date().toISOString().split('T')[0]}
                                onChange={(e) => setPaidOn(e.target.value)}
                                className="border border-gray-300 rounded px-2 py-1.5 text-sm text-gray-800"
                                data-testid="record-payment-date"
                            />
                        </label>
                    </div>
                    {error && <p className="text-xs text-red-600" data-testid="record-payment-error">{error}</p>}
                    <p className="text-xs text-gray-500">Extends the {plan.startsWith('child_') ? "children's membership" : 'membership'} by {plan.includes('annual') ? '365' : '30'} days — from its expiry if still covered on the paid date. No date + expired with books still out → counts from the day the membership ended.</p>
                    <button
                        onClick={submit} disabled={saving}
                        className="px-4 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white text-sm rounded disabled:opacity-50"
                        data-testid="record-payment-submit"
                    >
                        {saving ? 'Recording…' : 'Record payment'}
                    </button>
                </div>
            )}

            {payments.length === 0 ? (
                <p className="text-xs text-gray-500 italic">No successful payments on record.</p>
            ) : (
                <ul className="divide-y divide-emerald-100 max-h-48 overflow-y-auto bg-white rounded-lg border border-emerald-100">
                    {payments.map((p) => (
                        <li key={p.id} className="px-3 py-2 text-sm flex items-start justify-between gap-2" data-testid={`payment-row-${p.id}`}>
                            <div>
                                <span className="font-medium text-gray-800">₹{((p.amount || 0) / 100).toFixed(0)}</span>
                                <span className="text-gray-500"> — {PLAN_LABELS[p.plan] ? PLAN_LABELS[p.plan].split(' ₹')[0] : (p.plan || 'subscription')}{p.child_name ? ` (${p.child_name})` : ''}</span>
                                <span className={`ml-2 text-[11px] font-semibold rounded-full px-2 py-0.5 ${p.manual ? 'bg-amber-100 text-amber-700' : 'bg-blue-100 text-blue-700'}`}>
                                    {p.manual ? (p.payment_method || 'manual') : 'razorpay'}
                                </span>
                                {(p.notes || p.recorded_by_name) && (
                                    <span className="block text-xs text-gray-400">
                                        {p.manual && p.recorded_by_name ? `by ${p.recorded_by_name}` : ''}{p.notes ? `${p.recorded_by_name && p.manual ? ' — ' : ''}${p.notes}` : ''}
                                    </span>
                                )}
                            </div>
                            <span className="text-xs text-gray-500 shrink-0">{p.created_at ? new Date(p.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : ''}</span>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}
